/**
 * Geometrie et viewport. Tout ce qui est logique de jeu est en normalise 0-1 ;
 * les pixels n'apparaissent qu'ici et dans les renderers.
 */
import type { DrawZone } from '@retusch/shared';
import { EASE, FIT_PADDING, MAX_ZOOM_FACTOR, MIN_ZOOM_FACTOR } from './renderConfig.js';

/** Rectangle en pixels de l'image source. */
export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface Vec2 {
  readonly x: number;
  readonly y: number;
}

/**
 * Transformation image -> ecran : screen = image * scale + translate.
 * `scale` est en px CSS par px d'image.
 */
export interface Viewport {
  readonly scale: number;
  readonly tx: number;
  readonly ty: number;
}

export interface Size {
  readonly width: number;
  readonly height: number;
}

export function zoneToRect(zone: DrawZone, image: Size): Rect {
  return {
    x: zone.x * image.width,
    y: zone.y * image.height,
    width: zone.width * image.width,
    height: zone.height * image.height,
  };
}

export function imageToScreen(point: Vec2, viewport: Viewport): Vec2 {
  return { x: point.x * viewport.scale + viewport.tx, y: point.y * viewport.scale + viewport.ty };
}

export function screenToImage(point: Vec2, viewport: Viewport): Vec2 {
  return { x: (point.x - viewport.tx) / viewport.scale, y: (point.y - viewport.ty) / viewport.scale };
}

/** Coordonnees image en pixels -> normalise 0-1 sur l'image. */
export function imageToNormalized(point: Vec2, image: Size): Vec2 {
  return { x: point.x / image.width, y: point.y / image.height };
}

/** Normalise sur l'image -> normalise sur la zone (0-1 dans le rect de zone). */
export function normalizedToZone(point: Vec2, zone: DrawZone): Vec2 {
  return { x: (point.x - zone.x) / zone.width, y: (point.y - zone.y) / zone.height };
}

/** Normalise sur la zone -> normalise sur l'image. */
export function zoneToNormalized(point: Vec2, zone: DrawZone): Vec2 {
  return { x: zone.x + point.x * zone.width, y: zone.y + point.y * zone.height };
}

export function isInsideZone(point: Vec2, zone: DrawZone): boolean {
  return (
    point.x >= zone.x &&
    point.x <= zone.x + zone.width &&
    point.y >= zone.y &&
    point.y <= zone.y + zone.height
  );
}

/** Distance d'un point au rectangle, en pixels image. 0 si le point est dedans. */
export function distanceToRect(point: Vec2, rect: Rect): number {
  const dx = Math.max(rect.x - point.x, 0, point.x - (rect.x + rect.width));
  const dy = Math.max(rect.y - point.y, 0, point.y - (rect.y + rect.height));
  return Math.hypot(dx, dy);
}

/** Echelle qui fait tenir le tableau entier dans la vue. */
export function fitScale(image: Size, view: Size): number {
  if (image.width <= 0 || image.height <= 0) return 1;
  return Math.min(view.width / image.width, view.height / image.height) * FIT_PADDING;
}

export function fitViewport(image: Size, view: Size): Viewport {
  const scale = fitScale(image, view);
  return {
    scale,
    tx: (view.width - image.width * scale) / 2,
    ty: (view.height - image.height * scale) / 2,
  };
}

/**
 * Viewport qui cadre `rect` sur `ratio` de la largeur de la vue, centre dans
 * `view`. `focusOffsetY` remonte le centre pour laisser la place a la toolbar.
 */
export function focusViewport(rect: Rect, view: Size, ratio: number, focusOffsetY = 0): Viewport {
  const byWidth = (view.width * ratio) / rect.width;
  const byHeight = (view.height * ratio) / rect.height;
  const scale = Math.min(byWidth, byHeight);
  const centerX = rect.x + rect.width / 2;
  const centerY = rect.y + rect.height / 2;
  return {
    scale,
    tx: view.width / 2 - centerX * scale,
    ty: view.height / 2 - focusOffsetY - centerY * scale,
  };
}

/**
 * Empeche de perdre le tableau : l'echelle reste dans les bornes et le centre de
 * l'image ne peut pas sortir de la vue.
 */
export function clampViewport(viewport: Viewport, image: Size, view: Size): Viewport {
  const base = fitScale(image, view);
  const scale = Math.min(
    Math.max(viewport.scale, base * MIN_ZOOM_FACTOR),
    base * MAX_ZOOM_FACTOR,
  );

  // Reajuste la translation autour du point fixe si l'echelle a ete bridee.
  const k = scale / viewport.scale;
  let tx = view.width / 2 - (view.width / 2 - viewport.tx) * k;
  let ty = view.height / 2 - (view.height / 2 - viewport.ty) * k;

  const drawnWidth = image.width * scale;
  const drawnHeight = image.height * scale;
  const marginX = view.width / 2;
  const marginY = view.height / 2;

  tx = Math.min(Math.max(tx, -drawnWidth + marginX), view.width - marginX);
  ty = Math.min(Math.max(ty, -drawnHeight + marginY), view.height - marginY);

  return { scale, tx, ty };
}

export function lerpViewport(from: Viewport, to: Viewport, t: number): Viewport {
  return {
    scale: from.scale + (to.scale - from.scale) * t,
    tx: from.tx + (to.tx - from.tx) * t,
    ty: from.ty + (to.ty - from.ty) * t,
  };
}

/**
 * Evaluation de la cubic-bezier du design system. Newton sur x puis lecture de
 * y : suffisant et sans allocation, on est appele a chaque frame.
 */
export function ease(t: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;

  const { x1, y1, x2, y2 } = EASE;
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;

  let guess = t;
  for (let i = 0; i < 6; i += 1) {
    const x = ((ax * guess + bx) * guess + cx) * guess - t;
    const dx = (3 * ax * guess + 2 * bx) * guess + cx;
    if (Math.abs(dx) < 1e-6) break;
    guess -= x / dx;
  }

  return ((ay * guess + by) * guess + cy) * guess;
}
